import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const CreateOrder = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    installationDate: '',
    storeInvoiceNumber: '',
    notes: ''
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const addItem = () => {
    setItems([
      { productId: '', serviceId: '', name: '', price: 0, spiff: 0, qty: 1, notes: '' },
      ...items
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === 'serviceId' && value) {
      const product = products.find(p => p.id === newItems[index].productId);
      if (product) {
        const service = product.services.find(s => s.id === value);
        if (service) {
          newItems[index].name = service.name;
          newItems[index].price = service.price;
          newItems[index].spiff = service.spiff;
          // Prefill item-specific notes from service if available
          if (typeof service.notes === 'string' && service.notes.length > 0) {
            newItems[index].notes = service.notes;
          }
        }
      }
    }

    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/orders`, {
        ...formData,
        items
      }, { withCredentials: true });
      toast.success('Order created successfully!');
      navigate('/app/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const total = items.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const totalSpiff = items.reduce((sum, item) => sum + (item.spiff * item.qty), 0);

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '32px' }}>Create Order</h1>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px' }}>Customer Information</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
            <div className="form-group">
              <label className="form-label">Customer Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                required
                data-testid="customer-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                required
                data-testid="customer-email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="form-input"
                value={formData.customerPhone}
                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                required
                data-testid="customer-phone-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Installation Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.installationDate}
                onChange={(e) => setFormData({ ...formData, installationDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
                data-testid="installation-date-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Store Invoice #</label>
              <input
                type="text"
                className="form-input"
                value={formData.storeInvoiceNumber}
                onChange={(e) => setFormData({ ...formData, storeInvoiceNumber: e.target.value })}
                placeholder="Optional"
                data-testid="store-invoice-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-input"
              value={formData.customerAddress}
              onChange={(e) => setFormData({ ...formData, customerAddress: e.target.value })}
              required
              rows={3}
              data-testid="customer-address-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notes (Optional)</label>
            <textarea
              className="form-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700' }}>Products & Services</h2>
            <button type="button" onClick={addItem} className="btn btn-primary" data-testid="add-item-button">
              <Plus size={16} />
              Add Item
            </button>
          </div>

          {items.map((item, index) => (
            <div key={index} style={{
              background: '#FAFAFA',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '16px',
              overflowX: 'auto'
            }} data-testid={`order-item-${index}`}>
              <div style={{ minWidth: '280px' }}>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label">Category</label>
                  <Select value={item.productId} onValueChange={(value) => updateItem(index, 'productId', value)}>
                    <SelectTrigger data-testid={`product-select-${index}`}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>{product.category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label">Service</label>
                  <Select value={item.serviceId} onValueChange={(value) => updateItem(index, 'serviceId', value)} disabled={!item.productId}>
                    <SelectTrigger data-testid={`service-select-${index}`}>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {item.productId && products.find(p => p.id === item.productId)?.services.map(service => (
                        <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'end' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: '0 0 80px' }}>
                    <label className="form-label">Qty</label>
                    <input
                      type="number"
                      className="form-input"
                      value={item.qty}
                      onChange={(e) => updateItem(index, 'qty', parseInt(e.target.value) || 1)}
                      min={1}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                    <label className="form-label">Price</label>
                    <div style={{ padding: '12px 16px', background: 'white', borderRadius: '10px', fontWeight: '600' }}>
                      ${item.price}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="btn btn-danger"
                    style={{ padding: '12px', flexShrink: 0 }}
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Item Notes Row (read-only, shown above Spiff row) */}
              {item.notes && (
                <div style={{ marginTop: '12px', padding: '12px', background: 'white', borderRadius: '8px' }}>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Notes</div>
                  <div style={{ fontSize: '13px', color: '#333', whiteSpace: 'pre-wrap' }}>{item.notes}</div>
                </div>
              )}

              {item.serviceId && (
                <div style={{ marginTop: '12px', padding: '12px', background: 'white', borderRadius: '8px' }}>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Spiff: ${item.spiff}</div>
                </div>
              )}
            </div>
          ))}

          {items.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              No items added. Click "Add Item" to start.
            </div>
          )}
        </div>

        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Subtotal:</span>
            <span style={{ fontSize: '18px', fontWeight: '700' }} data-testid="order-subtotal">${total.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Total Spiff:</span>
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#4CAF50' }} data-testid="order-total-spiff">${totalSpiff.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '2px solid #E0E0E0', paddingTop: '12px', marginTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '24px', fontWeight: '800' }}>Total:</span>
            <span style={{ fontSize: '24px', fontWeight: '800', color: '#FDB515' }} data-testid="order-total">${total.toFixed(2)}</span>
          </div>
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '16px' }} disabled={loading} data-testid="submit-order-button">
          {loading ? 'Creating Order...' : 'Create Order'}
        </button>
      </form>
    </div>
  );
};

export default CreateOrder;
